// src/pages/AboutPage.js
"use client";

import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/* ---------- Small UI Pieces ---------- */

const SectionWrap = ({ children }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);

const SectionTitle = ({ children, eyebrow, sub }) => (
  <div className="text-center mb-10">
    {eyebrow && (
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700 mb-2">
        {eyebrow}
      </p>
    )}
    <h2 className="heading-serif text-2xl sm:text-3xl font-bold text-gray-900">
      {children}
    </h2>
    {sub && (
      <p className="mt-3 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
        {sub}
      </p>
    )}
  </div>
);

const StatTile = ({ value, label }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
    <div className="heading-serif text-2xl sm:text-3xl font-bold text-blue-700">
      {value}
    </div>
    <div className="mt-1.5 text-sm text-gray-600">{label}</div>
  </div>
);

const ValueCard = ({ title, text, icon }) => (
  <div className="group rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg hover:border-blue-200">
    {icon && <div className="mb-3 text-blue-700">{icon}</div>}
    <h3 className="heading-serif text-lg font-bold text-gray-900">{title}</h3>
    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{text}</p>
  </div>
);

const TagBadge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] font-medium text-gray-700">
    {children}
  </span>
);

const SkillBadge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[10px] font-medium text-blue-700">
    {children}
  </span>
);

/* ---------- Team card (expanded bio layout) ---------- */

const TeamCard = ({ name, role, img, bio, skills = [] }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
    <div className="flex flex-col sm:flex-row gap-6 sm:items-start">
      <div className="mx-auto sm:mx-0 h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-gray-200">
        {img ? (
          <img
            src={img}
            alt={name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gray-100" />
        )}
      </div>
      <div className="text-center sm:text-left">
        <h4 className="heading-serif text-xl font-bold text-gray-900">{name}</h4>
        <p className="text-sm font-semibold text-blue-700 mt-0.5">{role}</p>
        {bio && (
          <p className="mt-3 text-sm text-gray-700 leading-relaxed">{bio}</p>
        )}
        {skills.length > 0 && (
          <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-1.5">
            {skills.map((s, i) => (
              <SkillBadge key={i}>{s}</SkillBadge>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

/* ---------- Page ---------- */

const AboutPage = () => {
  return (
    <section className="flex flex-col w-full bg-white text-gray-900">
      <Helmet>
        <title>
          About — Advisory & Consulting for Real Estate, Tech and Finance | Maoq Digital Inc.
        </title>
        <meta
          name="description"
          content="Canada-based advisory and consulting firm. Real estate advisory for hotels, plazas, and land; consulting for tech and financial industries; data center projects; and business & commercial banking operations."
        />
        <link rel="canonical" href="https://www.maoqdigital.com/about" />

        <meta property="og:type" content="profile" />
        <meta property="og:title" content="About — Maoq Digital Inc." />
        <meta
          property="og:description"
          content="Canada-first advisory: real estate, tech & financial consulting, data centers, and banking operations. Clean info and clear steps."
        />
        <meta property="og:url" content="https://www.maoqdigital.com/about" />
        <meta
          property="og:image"
          content="https://www.maoqdigital.com/images/linkpreview.png"
        />
        <meta property="og:image:alt" content="About Maoq Digital Inc." />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About — Maoq Digital Inc." />
        <meta
          name="twitter:description"
          content="Canada-first advisory: real estate, tech & financial consulting, data centers, and banking operations."
        />
        <meta
          name="twitter:image"
          content="https://www.maoqdigital.com/images/linkpreview.png"
        />

        {/* JSON-LD: AboutPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About Maoq Digital Inc.",
            url: "https://www.maoqdigital.com/about",
            description:
              "Advisory and consulting focused on real estate (hotels, plazas, land), tech and financial industries, data center projects, and business & commercial banking operations across Canada.",
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <div
        className="relative"
        style={{
          backgroundImage:
            'linear-gradient(rgba(6,35,70,.86), rgba(6,35,70,.9)), url("https://images.unsplash.com/photo-1533929736458-ca588d08c8be?q=80&w=1920&auto=format&fit=crop")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <SectionWrap>
          <div className="py-16 sm:py-20 text-white">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-200">
                Canada • Advisory & Consulting
              </p>
              <h1 className="heading-serif text-3xl sm:text-4xl md:text-5xl font-bold mt-3">
                About Maoq Digital Inc.
              </h1>
              <p className="mt-5 text-sm sm:text-base text-gray-100 leading-relaxed">
                We're a Canada-based advisory and consulting firm working at the
                intersection of <span className="font-semibold">real estate</span>,{" "}
                <span className="font-semibold">technology</span>, and{" "}
                <span className="font-semibold">finance</span>. We advise on{" "}
                <span className="font-semibold">branded hotels</span>,{" "}
                <span className="font-semibold">plazas</span>, and{" "}
                <span className="font-semibold">land</span>; consult for{" "}
                <span className="font-semibold">tech and financial industry</span>{" "}
                clients; and support{" "}
                <span className="font-semibold">
                  lending and business & commercial banking
                </span>{" "}
                engagements. Simple, steady, and private.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  to="/listings"
                  className="inline-flex items-center rounded-xl bg-white/95 px-5 py-2.5 text-sm font-semibold text-blue-900 hover:bg-white transition"
                >
                  View Listings
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 transition"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </SectionWrap>
      </div>

      {/* SNAPSHOT */}
      <SectionWrap>
        <div className="py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <StatTile value="Canada First" label="Local insight, global reach" />
            <StatTile
              value="Real Estate • Tech • Finance"
              label="Three connected practices"
            />
            <StatTile value="Private & Steady" label="Quiet process, clean info" />
          </div>
        </div>
      </SectionWrap>

      {/* OUR FOCUS */}
      <SectionWrap>
        <div className="py-12">
          <SectionTitle
            eyebrow="Our focus"
            children="What we work on"
            sub="A tight scope across three practices, so work stays fast and clear."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ValueCard
              title="Branded Hotels"
              text="Change of ownership, portfolio add-ons, upgrades, and re-flags across North America."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 20h18v-2H3v2Zm2-4h14V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v10Zm4-8h2v2H9V8Zm0 4h2v2H9v-2Zm4-4h2v2h-2V8Zm0 4h2v2h-2v-2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Plazas, Land & Sites"
              text="Neighbourhood plazas with stable tenants, plus transit-near and mixed-use sites with clear planning paths."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 10l9-7 9 7v10a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2V10Z" />
                </svg>
              }
            />
            <ValueCard
              title="Data Center Projects"
              text="Advisory, software integration, and infrastructure sourcing matched to project requirements."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 3h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm0 7h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Zm0 7h16a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Zm2-12h2v2H6V5Zm0 7h2v2H6v-2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Tech & Financial Consulting"
              text="Practical consulting for technology companies and financial services firms — systems, diligence, and operations."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2 1 7l11 5 9-4.09V17h2V7L12 2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Banking Operations"
              text="Consulting on business and commercial banking operations, lending files, and process reviews."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2 2 7v2h20V7L12 2ZM4 11v7h3v-7H4Zm6.5 0v7h3v-7h-3ZM17 11v7h3v-7h-3ZM2 20v2h20v-2H2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Build & Upgrade Capital"
              text="Equity or preferred for new builds and upgrades. Budget, timeline, and simple updates."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h18v2H3zM3 8h18v2H3zM3 13h18v2H3zM3 18h18v2H3z" />
                </svg>
              }
            />
          </div>
        </div>
      </SectionWrap>

      {/* PRINCIPLES */}
      <SectionWrap>
        <div className="py-12">
          <SectionTitle
            eyebrow="How we operate"
            children="Principles we work by"
            sub="Plain talk, clean files, fair process."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ValueCard
              title="Plain talk"
              text="We keep language simple and numbers straight. No fluff."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h16v12H5.17L4 17.17V4Z" />
                </svg>
              }
            />
            <ValueCard
              title="Proof first"
              text="Photos, rent rolls, records, and trailing numbers — shared early and clearly."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 6h18v2H3zm0 5h12v2H3zm0 5h18v2H3z" />
                </svg>
              }
            />
            <ValueCard
              title="Privacy matters"
              text="Tight NDAs, small circles, and private calls when needed."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1a5 5 0 0 0-5 5v2H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Z" />
                </svg>
              }
            />
          </div>
        </div>
      </SectionWrap>

      {/* FOOTPRINT */}
      <SectionWrap>
        <div className="py-12">
          <SectionTitle
            eyebrow="Footprint"
            children="Where we operate"
            sub="Canada-focused, with hotel transaction experience across North America and interest from U.S. and GCC capital."
          />
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap justify-center gap-2">
              {["BC", "AB", "SK", "MB", "ON", "QC", "NS", "NB"].map((p) => (
                <TagBadge key={p}>{p}</TagBadge>
              ))}
            </div>
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-600">
                Typical work: branded hotels, neighbourhood plazas, mixed-use land,
                data center projects, and banking operations consulting.
              </p>
            </div>
          </div>
        </div>
      </SectionWrap>

      {/* WHO WE WORK WITH */}
      <SectionWrap>
        <div className="py-12">
          <SectionTitle
            eyebrow="Clients"
            children="Who we work with"
            sub="Owners, operators, institutions, and investors who value a simple, steady process."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <ValueCard
              title="Hotel & plaza owners"
              text="Single assets or small portfolios; brand-flagged and independent."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2 21h20v-2H2v2Zm2-4h16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10Zm4-8h2v2H8V9Zm0 4h2v2H8v-2Zm6-4h2v2h-2V9Zm0 4h2v2h-2v-2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Tech companies"
              text="Data center projects, software integration, and infrastructure sourcing."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm4 15h8v2H8v-2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Banks & lenders"
              text="Consulting on business and commercial banking operations and lending files."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2 2 7v2h20V7L12 2ZM4 11v7h3v-7H4Zm6.5 0v7h3v-7h-3ZM17 11v7h3v-7h-3ZM2 20v2h20v-2H2Z" />
                </svg>
              }
            />
            <ValueCard
              title="Investors & developers"
              text="Family offices, funds, and builders looking for clear, well-run processes."
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1 3 5v6c0 5 4 9 9 12 5-3 9-7 9-12V5l-9-4Z" />
                </svg>
              }
            />
          </div>
        </div>
      </SectionWrap>

      {/* TEAM */}
      <SectionWrap>
        <div className="py-12">
          <SectionTitle
            eyebrow="Team"
            children="Hands-on and responsive"
            sub="A small team with depth across real estate, technology, and banking."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TeamCard
              name="Aum Thakar"
              role="Senior Consultant - Technology & Banking Advisory"
              img="/images/Aum.jpeg"
              bio="Aum comes from a technology background and has consulted for top banks on business and commercial banking operations. He specializes in data center projects - advising on delivery, software integration, and sourcing infrastructure to match project requirements - and has been part of branded hotel sales across North America."
              skills={[
                "Data Center Projects",
                "Software Integration",
                "Infrastructure Sourcing",
                "Business & Commercial Banking",
                "Branded Hotel Sales",
              ]}
            />
            <TeamCard
              name="Yash Sunilbhai Patel"
              role="Founding Member & Advisor"
              img=""
              bio="Yash supports buy-side and sell-side engagements and prepares investor materials, keeping every file clean and every process on track."
              skills={["Buy-Side Support", "Sell-Side Support", "Investor Materials"]}
            />
          </div>
        </div>
      </SectionWrap>

      {/* CTA */}
      <SectionWrap>
        <div className="pb-16">
          <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="heading-serif text-2xl sm:text-3xl font-bold">
                Let's talk about your property or project
              </h2>
              <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed">
                A hotel or plaza to sell, a data center project, or a banking
                operations question — tell us where you are and we'll map the next
                step.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center h-11 px-5 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition"
              >
                Book a consultation
              </Link>
              <a
                href="mailto:aumthakar@maoqdigital.com"
                className="inline-flex items-center justify-center h-11 px-5 rounded-xl bg-[#C6A16B] hover:bg-[#b38f59] text-black text-sm font-semibold transition"
              >
                Email us
              </a>
            </div>
          </div>
        </div>
      </SectionWrap>
    </section>
  );
};

export default AboutPage;